import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutGrid, History, User, Users, FileText, Settings, BarChart3, HardDrive,
  Building2, TrendingUp, Crown, ListChecks, Tag, Receipt, Sparkles, CalendarClock,
  LifeBuoy, MessageSquare, PackageSearch, GraduationCap, ToggleRight, ClipboardList, Coins,
  CreditCard, Video, Bot, FolderKanban, Zap,
  Bug,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useFeatures } from "../context/FeaturesContext";
import api from "../lib/api";
import { isTabActive, playPing, showDesktopNotification } from "../lib/notifications";
import { TenantLogo, useTenantBranding } from "./TenantLogo";

const MARA_LOGO = "/brand/mara-logo.png";

const NavItem = ({ to, icon: Icon, label, testId, end, badge }) => (
  <NavLink
    to={to} end={end} data-testid={testId}
    className={({ isActive }) => `wl-nav-item ${isActive ? "is-active" : ""}`}
  >
    <span className="wl-nav-item__icon" aria-hidden="true">
      <Icon strokeWidth={1.5} />
    </span>
    <span className="wl-nav-item__label">{label}</span>
    {badge != null && badge > 0 && (
      <span
        data-testid={`${testId}-badge`}
        className="wl-nav-item__badge"
      >
        {badge > 99 ? "99+" : badge}
      </span>
    )}
  </NavLink>
);

const DisabledNavItem = ({ icon: Icon, label, testId, hint = "Presto" }) => (
  <div
    data-testid={testId}
    title={hint}
    className="wl-nav-item is-disabled"
  >
    <span className="wl-nav-item__icon" aria-hidden="true">
      <Icon strokeWidth={1.5} />
    </span>
    <span className="wl-nav-item__label">{label}</span>
    <span className="wl-nav-item__hint">{hint}</span>
  </div>
);


const SectionLabel = ({ children, icon: Icon, iconColor }) => (
  <div className="wl-nav-section">
    {Icon && <Icon size={11} strokeWidth={2} style={{ color: iconColor, display: "inline", verticalAlign: "-1px", marginRight: 4 }} />}
    <span>{children}</span>
  </div>
);

export const Sidebar = ({ mobileOpen = false, onMobileClose }) => {
  const { user } = useAuth();
  const { features } = useFeatures();
  const branding = useTenantBranding();
  const isSuperAdmin = user?.role === "super_admin";
  const isAdmin = user?.role === "admin" || isSuperAdmin;

  // Helper: nasconde una voce se il flag è OFF. Super admin vede sempre tutto.
  const showIf = (moduleName) => isSuperAdmin || (features && features[moduleName]);

  // Notifiche desktop tab-level: agganciate al polling esistente (nessun polling extra).
  // Scattano solo se il tab NON è attivo (visibile + a fuoco) e la preferenza utente è ON.
  const navigate = useNavigate();
  const prevSupportRef = useRef(null);
  const prevInternalRef = useRef(null);
  const maybeNotify = (kind, prevRef, count) => {
    const prev = prevRef.current;
    prevRef.current = count;
    if (prev === null || count <= prev) return;
    if (!user?.desktop_notifications || isTabActive()) return;
    const delta = count - prev;
    const cfg = kind === "support"
      ? {
          title: "Supporto — Wattline",
          body: delta === 1 ? "Hai un nuovo aggiornamento sui ticket di supporto." : `Hai ${delta} nuovi aggiornamenti sui ticket di supporto.`,
          path: "/support",
        }
      : {
          title: "Messaggi interni — Wattline",
          body: delta === 1 ? "Hai un nuovo messaggio interno." : `Hai ${delta} nuovi messaggi interni.`,
          path: "/messages",
        };
    showDesktopNotification({
      title: cfg.title,
      body: cfg.body,
      tag: `wl-notif-${kind}`,
      onClick: () => navigate(cfg.path),
    });
    if (user?.notification_sound) playPing();
  };

  // Badge notifiche Presenze (visibile solo agli admin)
  const [attendanceBadge, setAttendanceBadge] = useState(0);
  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/attendance/notifications/unread-count");
        if (!cancelled) setAttendanceBadge(data?.count || 0);
      } catch {
        // silente
      }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000); // poll ogni 60s
    return () => { cancelled = true; clearInterval(id); };
  }, [isAdmin]);

  // Badge notifiche Supporto (visibile a tutti: cliente vede risposte/cambio stato, super_admin vede nuovi ticket)
  const [supportBadge, setSupportBadge] = useState(0);
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/support/notifications/unread-count");
        if (!cancelled) {
          const c = data?.count || 0;
          maybeNotify("support", prevSupportRef, c);
          setSupportBadge(c);
        }
      } catch {
        // silente
      }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    window.addEventListener("wl-unread-refresh", fetchCount);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener("wl-unread-refresh", fetchCount); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Badge notifiche Messaggi interni (NON visibile ai super_admin: non accedono al canale)
  const [internalBadge, setInternalBadge] = useState(0);
  useEffect(() => {
    if (!user || isSuperAdmin) return undefined;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/internal/notifications/unread-count");
        if (!cancelled) {
          const c = data?.count || 0;
          maybeNotify("internal", prevInternalRef, c);
          setInternalBadge(c);
        }
      } catch {
        // silente
      }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    window.addEventListener("wl-unread-refresh", fetchCount);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener("wl-unread-refresh", fetchCount); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isSuperAdmin]);

  // Badge Documenti "da leggere" (non super_admin)
  const [docsBadge, setDocsBadge] = useState(0);
  useEffect(() => {
    if (!user || isSuperAdmin || !(features && features.documenti)) return undefined;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/docs/unread-count");
        if (!cancelled) setDocsBadge(data?.count || 0);
      } catch {
        // silente
      }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    window.addEventListener("wl-unread-refresh", fetchCount);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener("wl-unread-refresh", fetchCount); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isSuperAdmin, features]);

  // Contatore non letti nel titolo della scheda: "(N) Wattline".
  // Agganciato agli stessi contatori del polling; la sezione attualmente
  // visitata non contribuisce (azzerato in visita).
  const location = useLocation();
  useEffect(() => {
    const total =
      (location.pathname.startsWith("/support") ? 0 : supportBadge) +
      (location.pathname.startsWith("/messages") ? 0 : internalBadge);
    document.title = total > 0
      ? `(${total > 99 ? "99+" : total}) Wattline`
      : "Wattline";
    return () => { document.title = "Wattline"; };
  }, [supportBadge, internalBadge, location.pathname]);

  return (
    <aside
      data-testid="sidebar"
      data-mobile-open={mobileOpen ? "true" : "false"}
      className={`wl-sidebar
        md:static md:translate-x-0 md:z-auto
        fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 ease-out
        ${mobileOpen ? "translate-x-0 shadow-xl" : "-translate-x-full md:translate-x-0"}
      `}
    >
      <div className="wl-sidebar__brand">
        {branding?.has_logo ? (
          <TenantLogo maxHeight={56} maxWidth="100%" testId="sidebar-tenant-logo" />
        ) : (
          <div className="wl-sidebar__brand-title" data-testid="sidebar-tenant-name">
            {branding?.org_name || "Wattline"}
          </div>
        )}
        {Boolean(branding?.has_logo || branding?.org_name) && (
          <div className="wl-sidebar__brand-tagline" data-testid="sidebar-by-wattline">by Wattline</div>
        )}
      </div>

      <nav className="wl-sidebar__nav">
        {/* IL MIO LAVORO: operatività quotidiana della persona */}
        <SectionLabel>Il mio lavoro</SectionLabel>
        <NavItem to="/dashboard" icon={LayoutGrid} label="Dashboard" testId="nav-dashboard" />
        {showIf("presenze") && (
          <NavItem
            to="/attendance"
            icon={CalendarClock}
            label="Presenze"
            testId="nav-attendance"
            badge={isAdmin ? attendanceBadge : null}
          />
        )}
        {showIf("checklist") && !isSuperAdmin && (
          <NavItem
            to="/checklist"
            icon={ListChecks}
            label="Checklist"
            testId="nav-checklist"
          />
        )}
        {showIf("messaggi") && !isSuperAdmin && (
          <NavItem
            to="/messages"
            icon={MessageSquare}
            label="Messaggi interni"
            testId="nav-internal-messages"
            badge={internalBadge}
          />
        )}
        {showIf("ticket") && (
          <NavItem
            to="/support"
            icon={LifeBuoy}
            label="Supporto"
            testId="nav-support"
            badge={supportBadge}
          />
        )}
        {showIf("riunioni") && (
          <NavItem
            to="/meetings"
            icon={Video}
            label="Riunioni"
            testId="nav-meetings"
          />
        )}
        {showIf("formazione") && (
          <NavItem
            to="/formazione"
            icon={GraduationCap}
            label="Formazione"
            testId="nav-formazione"
          />
        )}
        {showIf("documenti") && !isSuperAdmin && (
          <NavItem
            to="/documents"
            icon={FolderKanban}
            label="Documenti"
            testId="nav-documents"
            badge={docsBadge}
          />
        )}
        <NavItem to="/profile" icon={User} label="Profilo" testId="nav-profile" />

        {/* STRUMENTI: catalogo piattaforma, visure/verifiche e strumenti di lavoro */}
        <SectionLabel>Strumenti</SectionLabel>
        {showIf("ai_tools") && (
          <NavItem to="/assistant" icon={Sparkles} label="AI aziendale" testId="nav-assistant" />
        )}
        <NavItem to="/services" icon={PackageSearch} label="Servizi" testId="nav-services" />
        {showIf("verifica") && (
          <NavItem to="/history" icon={History} label="Storico richieste" testId="nav-history" />
        )}
        {isAdmin && (
          <NavItem to="/admin/storage" icon={HardDrive} label="Storage PDF" testId="nav-admin-storage" />
        )}

        {/* ENERGIA: solo Mercato — Punti e Flussi sono spenti per tutti
            (PUNTI_FLUSSI_SPENTI nel backend, voci e rotte rimosse qui) */}
        {showIf("energia") && (
          <>
            <SectionLabel>Energia</SectionLabel>
            <NavItem to="/energia/mercato" icon={Zap} label="Mercato" testId="nav-energia-mercato" />
          </>
        )}

        {isAdmin && (
          <>
            <SectionLabel>Amministrazione</SectionLabel>
            <NavItem to="/admin/users" icon={Users} label="Utenti" testId="nav-admin-users" />
            <NavItem to="/admin/services" icon={Settings} label="Servizi" testId="nav-admin-services" />
            <NavItem to="/admin/logs" icon={FileText} label="Log attività" testId="nav-admin-logs" />
            <NavItem to="/admin/analytics" icon={BarChart3} label="Analytics" testId="nav-admin-analytics" />
            {showIf("ai_tools") && !isSuperAdmin && (
              <NavItem to="/admin/ai" icon={Bot} label="Uso dell'AI" testId="nav-admin-ai" />
            )}
          </>
        )}

        {isSuperAdmin && (
          <>
            <SectionLabel icon={Crown} iconColor="var(--wl-brand-yellow-ink)">Wattline</SectionLabel>
            <NavItem to="/superadmin/organizations" icon={Building2} label="Organizzazioni" testId="nav-sa-orgs" />
            <NavItem to="/superadmin/tenant-features" icon={ToggleRight} label="Funzioni per cliente" testId="nav-sa-tenant-features" />
            <NavItem to="/superadmin/formazione" icon={GraduationCap} label="Catalogo formazione" testId="nav-sa-formazione" />
            <NavItem to="/superadmin/discovery" icon={ClipboardList} label="Discovery" testId="nav-sa-discovery" />
            <NavItem to="/superadmin/revenue" icon={TrendingUp} label="Ricavi" testId="nav-sa-revenue" />
            <NavItem to="/superadmin/subscriptions" icon={CreditCard} label="Abbonamenti" testId="nav-sa-subscriptions" />
            <NavItem to="/superadmin/requests" icon={ListChecks} label="Tutte le richieste" testId="nav-sa-requests" />
            <NavItem to="/superadmin/users" icon={Users} label="Tutti gli utenti" testId="nav-sa-users" />
            <NavItem to="/superadmin/pricing" icon={Tag} label="Piani e prezzi" testId="nav-sa-pricing" />
            <NavItem to="/superadmin/costs" icon={Receipt} label="Costi OpenAPI" testId="nav-sa-costs" />
            <NavItem to="/superadmin/ai-config" icon={Sparkles} label="Assistente AI" testId="nav-sa-ai-config" />
            <NavItem to="/superadmin/ai-costs" icon={Coins} label="Costi AI" testId="nav-sa-ai-costs" />
            <NavItem to="/superadmin/errori" icon={Bug} label="Registro errori" testId="nav-sa-errors" />
          </>
        )}
      </nav>

      <div className="wl-sidebar__footer">
        <div className="flex items-center justify-center gap-1" style={{ opacity: 0.55 }}>
          <img src={MARA_LOGO} alt="MARA" style={{ height: 9, width: "auto" }} data-testid="sidebar-mara-logo" />
          <span className="wl-sidebar__footer-hint" style={{ fontSize: 9 }}>created Wattline</span>
        </div>
      </div>
    </aside>
  );
};
